// src/features/notifications/api/notificationApi.ts

import { apiClient } from '@/config/api';
import type { Notification, TaskReminder, ReminderHealthStatus } from '../types/notification.types';

const unwrapResponse = <T>(response: T | { data?: T }): T => {
  const maybeWrapped = response as { data?: T };
  return typeof maybeWrapped?.data !== 'undefined' ? maybeWrapped.data : (response as T);
};

export const notificationApi = {
  list: async (params?: { page?: number; limit?: number; unread_only?: string }) => {
    const response = await apiClient.get<{ notifications: Notification[]; total: number }>(
      '/api/v1/notifications',
      params as Record<string, unknown>,
    );
    return unwrapResponse(response);
  },

  getUnreadCount: async () => {
    const response = await apiClient.get<{ count: number }>('/api/v1/notifications/unread-count');
    return unwrapResponse(response);
  },

  markAsRead: (id: string) =>
    apiClient.patch<{ success: boolean }>(`/api/v1/notifications/${id}/read`, {}),

  markAllRead: () =>
    apiClient.patch<{ success: boolean }>('/api/v1/notifications/read-all', {}),

  deleteNotification: (id: string) =>
    apiClient.delete<{ success: boolean }>(`/api/v1/notifications/${id}`),

  clearRead: () =>
    apiClient.delete<{ success: boolean; cleared: number }>('/api/v1/notifications/clear-read'),

  getReminderHealth: async () => {
    const response = await apiClient.get<ReminderHealthStatus>('/api/v1/notifications/health');
    return unwrapResponse(response);
  },

  // Reminders
  getReminders: async (taskId: string) => {
    const response = await apiClient.get<{ reminders: TaskReminder[] }>(`/api/v1/tasks/${taskId}/reminders`);
    return unwrapResponse(response);
  },

  createReminder: (taskId: string, data: { scheduled_time: string; method?: string }) =>
    apiClient.post<{ reminder: TaskReminder }>(`/api/v1/tasks/${taskId}/reminders`, data),

  snoozeReminder: (reminderId: string, snooze_until: string) =>
    apiClient.patch<{ reminder: TaskReminder }>(`/api/v1/reminders/${reminderId}/snooze`, { snooze_until }),

  dismissReminder: (reminderId: string) =>
    apiClient.patch<{ success: boolean }>(`/api/v1/reminders/${reminderId}/dismiss`, {}),
};
